import React, { useMemo } from 'react';
import { useGetTradeOffersByStatusQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod } from 'types/interfaces/admin/analytics';
import StackedBarChart, {
  StackedBarChartRow,
  StackedBarTotals
} from 'components/admin/charts/StackedBarChart';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

// Semantic colors keyed on the raw TradeOfferStatus names the backend emits. A named palette is
// wrong here: those assign by segment order, so a status would change color as volumes shift.
// Green/blue match the positive/send-cards colors already used by TradeInitiationSourcesChart.
// The reds are an admin readout of genuinely negative outcomes, not a user-facing value display.
const OFFER_STATUS_COLORS: Record<string, string> = {
  ACCEPTED: '#22C55E', // accepted
  DECLINED: '#EF4444', // recipient rejected
  CANCELLED: '#F59E0B', // proposer withdrew
  EXPIRED: '#FB923C', // recipient never acted
  FAILED: '#991B1B', // accept lost the revalidation race — a system defect, not a decision
  PENDING: '#3B82F6' // still open
};

// ACCEPTED -> "Accepted". Applied only for display; the raw name stays the dataKey and color key.
const titleCase = (status: string): string =>
  status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();

const periodNoun = (period: AnalyticsPeriod): string =>
  period === 7 ? 'week' : period === 30 ? 'month' : 'year';

const TradeOffersByStatusChart: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error, refetch } = useGetTradeOffersByStatusQuery({ period });

  const rows = useMemo<StackedBarChartRow[] | undefined>(
    () => data?.data.map((r) => ({ date: r.date, dimension: r.status, value: r.count })),
    [data]
  );

  // Six segments exceeds the shared component's 4-swatch default legend, so render all of them
  // in the server's order with their period totals.
  const legend = useMemo(() => {
    if (!data) return undefined;
    const totalByStatus = new Map(data.totals.map((t) => [t.status, t.count]));
    return (
      <div className="chart-legend-multi">
        {data.statusOrder.map((status) => (
          <div key={status} className="chart-legend">
            <span
              className="legend-dot"
              style={{ backgroundColor: OFFER_STATUS_COLORS[status] ?? '#6B7280' }}
            />
            <span className="legend-label">
              {titleCase(status)} ({(totalByStatus.get(status) ?? 0).toLocaleString()})
            </span>
          </div>
        ))}
      </div>
    );
  }, [data]);

  // Accept rate is the question this chart exists to answer. Read the counts off the response
  // rather than re-summing the rows client-side.
  const footerTemplate = useMemo(
    () => (_totals: StackedBarTotals, p: AnalyticsPeriod) => {
      if (!data) return null;
      const countFor = (status: string) =>
        data.totals.find((t) => t.status === status)?.count ?? 0;
      const accepted = countFor('ACCEPTED');
      const acceptRate = data.total > 0 ? Math.round((accepted / data.total) * 100) : 0;
      return `Total: ${data.total.toLocaleString()} trade offers in the last ${periodNoun(p)} — ${accepted.toLocaleString()} accepted (${acceptRate}%), ${countFor('DECLINED').toLocaleString()} declined, ${countFor('CANCELLED').toLocaleString()} cancelled`;
    },
    [data]
  );

  return (
    <StackedBarChart
      title="Trade Offers Initiated"
      subtitle="Daily offers created, by current status (UTC)"
      period={period}
      onPeriodChange={onPeriodChange}
      data={rows}
      isLoading={isLoading}
      error={!!error}
      onRetry={refetch}
      colorPalette={OFFER_STATUS_COLORS}
      // From the response, not a local constant: the backend appends any future status here, so
      // a hardcoded order would leave a new status unranked and randomly colored.
      segmentOrder={data?.statusOrder}
      legend={legend}
      footerTemplate={footerTemplate}
      yAxisLabel="Trade Offers"
      valueNoun="trade offers"
      emptyMessage="No trade offers initiated in this period"
    />
  );
};

export default TradeOffersByStatusChart;
